import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Loader2, CalendarCheck, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { API, SERVICES } from "../lib/api";

const TIMES = ["Morning (8am-12pm)", "Afternoon (12pm-4pm)", "Late Afternoon (4pm-6pm)"];

export default function AssessmentPage() {
  const nav = useNavigate();
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const [date, setDate] = useState(tomorrow);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    service_type: "", site_address: "",
    preferred_time: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Pick a date.");
    if (!form.service_type) return toast.error("Select a service.");
    if (!form.preferred_time) return toast.error("Select a preferred time.");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads/assessment`, {
        ...form,
        preferred_date: format(date, "yyyy-MM-dd"),
      });
      toast.success(res.data.message || "Assessment scheduled.");
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="assessment-success">
        <CheckCircle2 size={56} className="text-[#1E3A6B] mx-auto" />
        <h1 className="font-display font-bold text-3xl mt-6 text-slate-900">Assessment requested.</h1>
        <p className="mt-3 text-slate-600">We'll confirm your appointment by email within one business day.</p>
        <Button onClick={() => nav("/")} className="mt-8 rounded-none bg-slate-900 hover:bg-slate-800">Back to home</Button>
      </div>
    );
  }

  return (
    <div data-testid="assessment-page" className="bg-[#FAFAFA]">
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-14">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#1E3A6B] mb-3">
            <CalendarCheck size={14} /> — Free On-Site Visit
          </div>
          <h1 className="font-display font-black tracking-tight text-4xl lg:text-5xl text-slate-900">
            Schedule a site assessment.
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
            We'll walk your site, measure existing infrastructure, and deliver an
            engineered scope-of-work — no obligation, no charge.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <form onSubmit={submit} className="bg-white border border-slate-200 p-6 md:p-10 grid lg:grid-cols-12 gap-10" data-testid="assessment-form">
          <div className="lg:col-span-5">
            <h2 className="font-display font-bold text-xl text-slate-900 mb-2">Pick a date</h2>
            <p className="text-sm text-slate-500 mb-4">We'll confirm exact time after review.</p>
            <div className="border border-slate-200 p-2 inline-block">
              <Calendar
                data-testid="assessment-calendar"
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
              />
            </div>
            <div className="mt-4">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-700">Preferred time *</Label>
              <Select value={form.preferred_time} onValueChange={(v) => upd("preferred_time", v)}>
                <SelectTrigger data-testid="assessment-time-select" className="rounded-none border-slate-300 mt-1.5">
                  <SelectValue placeholder="Select a time window" />
                </SelectTrigger>
                <SelectContent>
                  {TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-5">
            <h2 className="font-display font-bold text-xl text-slate-900">Your details</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name *"><Input data-testid="assess-input-name" required value={form.name} onChange={(e) => upd("name", e.target.value)} /></Field>
              <Field label="Company"><Input data-testid="assess-input-company" value={form.company} onChange={(e) => upd("company", e.target.value)} /></Field>
              <Field label="Email *"><Input data-testid="assess-input-email" type="email" required value={form.email} onChange={(e) => upd("email", e.target.value)} /></Field>
              <Field label="Phone *"><Input data-testid="assess-input-phone" required value={form.phone} onChange={(e) => upd("phone", e.target.value)} /></Field>
            </div>
            <Field label="Primary service of interest *">
              <Select value={form.service_type} onValueChange={(v) => upd("service_type", v)}>
                <SelectTrigger data-testid="assess-service-select" className="rounded-none border-slate-300">
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICES.map((s) => <SelectItem key={s.slug} value={s.name}>{s.name}</SelectItem>)}
                  <SelectItem value="Multiple Services">Multiple Services</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Site address *">
              <Input data-testid="assess-input-address" required value={form.site_address} onChange={(e) => upd("site_address", e.target.value)} />
            </Field>
            <Field label="Notes (optional)">
              <Textarea data-testid="assess-input-notes" rows={4} value={form.notes} onChange={(e) => upd("notes", e.target.value)} placeholder="Anything we should know — access, parking, current systems…" />
            </Field>

            <div className="flex items-center justify-between border-t border-slate-200 pt-5">
              <p className="text-sm text-slate-500">{date ? `Selected: ${format(date, "EEEE, MMM d")}` : "Pick a date →"}</p>
              <Button data-testid="assess-submit-btn" type="submit" disabled={loading} className="rounded-none bg-[#1E3A6B] hover:bg-[#172F58] h-12 px-8 text-base">
                {loading ? <><Loader2 size={16} className="animate-spin mr-2" />Booking…</> : "Book Assessment"}
              </Button>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-mono uppercase tracking-wider text-slate-700">{label}</Label>
      {children}
    </div>
  );
}
